/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * God Mode Inspector — Platform Owner read-only view of any org's module data.
 * Accessible only by SUPER_ADMIN. No org consent required.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { APP_REGISTRY, APP_IDS } from '../domain/entities';
import { PLATFORM_MODULES } from '../core/appRegistry';
import type { MenuProfile } from '../modules/menu-manage/types';
import { getFinancialBreakdown } from '../modules/menu-manage/services/PromoService';
import { RequestStatus } from '../modules/rider-ops/types';
import { useGodModeData } from '../ui/useGodModeData';
import { getRepository } from '../config/repository';
import { apiFetch } from '../data/ApiModuleRepository';
import type { GodModeSnapshot } from '../ui/useGodModeData';
import type { User } from '../domain/entities';
import {
  ArrowLeft, Eye, Lock, Clock, Search, Users, Settings as SettingsIcon,
  Package, BarChart3, Boxes, Banknote, FileText, AlertCircle, BookOpen, LifeBuoy,
  Megaphone, CheckCircle2, XCircle, ArrowRight, Layers, Unlock, Globe, RefreshCw, ChevronRight,
  Download, ToggleLeft, ToggleRight, ImageIcon, RotateCcw, Info, Check, Ban, Link2,
} from 'lucide-react';
import { Campaign, CampaignSubmission, SubmissionStatus } from '../modules/aggregator/types';
import { DEPARTMENT_STAGES, Department } from '../domain/entities';
import type { Brand } from '../domain/entities';
import { cn } from '../ui/utils';
import { LiveToggleSwitch } from '../ui/LiveToggleSwitch';
import { flattenBranches, getBranchById, getBrandByBranchId } from '../core/brandBranches';
import { computeCampaignReadiness, type CampaignReadiness } from '../modules/aggregator/utils';
import { DispatchService, DEFAULT_DISPATCH_CONFIG, ALL_COLUMNS_ON, ALL_READINESS_COLUMNS_ON, type DispatchConfig, type ColumnSelection, type ReadinessColumnSelection } from '../modules/aggregator/services/DispatchService';

// ---------------------------------------------------------------------------
// Module icon registry
// ---------------------------------------------------------------------------

const MODULE_ICONS: Record<string, React.ElementType> = {
  [APP_IDS.RIDER_MANAGER]:     Package,
  [APP_IDS.CAMPAIGN_MANAGER]: Globe,
  [APP_IDS.MENU_MANAGE]:      BookOpen,
  [APP_IDS.SUPPORT_DESK]:     LifeBuoy,
};

// ---------------------------------------------------------------------------
// Audit helpers
// ---------------------------------------------------------------------------

function writeInspectionLog(
  user: User | null,
  orgId: string,
  orgName: string,
  moduleId: string,
) {
  // Fire-and-forget to server audit log
  apiFetch('/api/system/audit-log', {
    method: 'POST',
    body: JSON.stringify({
      action: [
        'SUPER_ADMIN_INSPECTION_START',
        '\u2014 Inspecting',
        orgName,
        '(' + orgId + ')',
        moduleId ? '\u203a ' + moduleId : '(app-selector)',
      ].join(' '),
    }),
  }).catch(() => {});
}

// ---------------------------------------------------------------------------
// Context Bar
// ---------------------------------------------------------------------------

function GodModeContextBar({
  orgName,
  moduleName,
  onExit,
}: {
  orgName: string;
  moduleName?: string;
  onExit: () => void;
}) {
  return (
    <div className="sticky top-0 z-50 flex items-center justify-between gap-4 px-6 py-3 bg-amber-500 text-white shadow-xl border-b-2 border-amber-600">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-950">
            Management View
          </span>
        </div>
        <span className="text-amber-700 font-light text-lg select-none">|</span>
        <span className="text-sm truncate">
          <span className="text-amber-950/70">Viewing </span>
          <span className="font-bold text-amber-950">{orgName}</span>
          <span className="text-amber-950/70"> Data</span>
          {moduleName && (
            <>
              <span className="mx-2 text-amber-800">&rsaquo;</span>
              <span className="font-bold text-amber-950">{moduleName}</span>
            </>
          )}
        </span>
      </div>
      <button
        onClick={onExit}
        className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-800 text-xs font-bold transition-colors text-white"
      >
        <ArrowLeft size={12} />
        Exit Inspection
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module Not Licensed
// ---------------------------------------------------------------------------

function ModuleNotLicensedCard({ moduleName }: { moduleName: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center px-6">
      <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mb-5">
        <Lock size={28} className="text-zinc-400" />
      </div>
      <h3 className="text-lg font-bold text-zinc-700 mb-2">Module Not Licensed</h3>
      <p className="text-sm text-zinc-500 max-w-xs">
        <span className="font-semibold">{moduleName}</span> is not enabled for this organisation.
        Enable it from the Organisation management screen.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// God Mode Rider View — read-only data inspector tabs
// ---------------------------------------------------------------------------

function GodModeRiderView({ data }: { data: GodModeSnapshot }) {
  const [tab, setTab] = useState<'riders' | 'requests' | 'settings'>('riders');
  const [search, setSearch] = useState('');

  const allBranches = flattenBranches(data.brands);
  const resolveBranch = (rider: typeof data.riders[number]) => {
    const branch = rider.branchId ? getBranchById(data.brands, rider.branchId) ?? allBranches.find(b => b.code.toLowerCase() === rider.branchId!.toLowerCase()) : undefined;
    const brand = branch ? getBrandByBranchId(data.brands, branch.id) : undefined;
    return { branchName: branch?.name ?? '—', branchCode: branch?.code ?? '—', brandName: brand?.name ?? '—' };
  };

  const filtered = data.riders.filter(r => {
    const bm = resolveBranch(r);
    return r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.mobile.includes(search) ||
      String(r.fileNumber).includes(search) ||
      bm.branchCode.toLowerCase().includes(search.toLowerCase()) ||
      bm.branchName.toLowerCase().includes(search.toLowerCase());
  });

  const pendingCount = data.requests.filter(r => r.status === RequestStatus.PENDING).length;

  const tabs = [
    { id: 'riders'   as const, label: 'Riders',   Icon: Users,        badge: data.riders.length },
    { id: 'requests' as const, label: 'Requests', Icon: FileText,     badge: pendingCount > 0 ? pendingCount : undefined },
    { id: 'settings' as const, label: 'Settings', Icon: SettingsIcon, badge: undefined as number | undefined },
  ];

  return (
    <div className="flex flex-col h-full">

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-zinc-200 bg-white px-6">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-800',
            )}
          >
            <t.Icon size={14} />
            {t.label}
            {t.badge !== undefined && (
              <span className={cn(
                'ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                t.id === 'requests' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500',
              )}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Riders tab ─────────────────────────────────────────── */}
      {tab === 'riders' && (
        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, mobile, file no, branch..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>
            <span className="text-xs text-zinc-400">{filtered.length} of {data.riders.length} riders</span>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  {['File No', 'Name', 'Mobile', 'Branch', 'Brand', 'Contract', 'Status', 'Created'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-zinc-400 text-sm">
                      No riders found
                    </td>
                  </tr>
                ) : filtered.map(rider => {
                  const bm = resolveBranch(rider);
                  return (
                  <tr key={rider.id} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600">
                      {String(rider.fileNumber).padStart(4, '0')}
                    </td>
                    <td className="px-4 py-3 font-semibold text-zinc-900">{rider.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-indigo-700 bg-indigo-50/40">
                      {rider.mobile}
                    </td>
                    <td className="px-4 py-3 text-zinc-600">
                      <div className="text-sm">{bm.branchName}</div>
                      <div className="text-xs text-zinc-400">{bm.branchCode}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{bm.brandName}</td>
                    <td className="px-4 py-3 text-zinc-600">{rider.contract}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full',
                        rider.isActive
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-zinc-100 text-zinc-500',
                      )}>
                        <span className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          rider.isActive ? 'bg-emerald-500' : 'bg-zinc-400',
                        )} />
                        {rider.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {new Date(rider.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Requests tab ───────────────────────────────────────── */}
      {tab === 'requests' && (
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  {['Type', 'Rider Name', 'Branch', 'Requester', 'Status', 'Submitted'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-zinc-400 text-sm">
                      No requests
                    </td>
                  </tr>
                ) : data.requests.map(req => (
                  <tr key={req.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">
                        {req.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-zinc-900">
                      {req.riderData?.name ?? (req.riderId ? 'Rider ' + req.riderId : '\u2014')}
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs font-mono">
                      {(() => { const b = req.branchId ? getBranchById(data.brands, req.branchId) : undefined; return b ? `${b.name} (${b.code})` : req.branchId ?? '\u2014'; })()}
                    </td>
                    <td className="px-4 py-3 text-zinc-600 text-xs">{req.requesterName}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'text-[11px] font-bold px-2 py-0.5 rounded-full',
                        req.status === RequestStatus.PENDING
                          ? 'bg-amber-100 text-amber-700'
                          : req.status === RequestStatus.APPROVED
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700',
                      )}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {new Date(req.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Settings tab ───────────────────────────────────────── */}
      {tab === 'settings' && (
        <div className="flex-1 overflow-auto p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h4 className="text-sm font-bold text-zinc-700 mb-3">Brand → Branch Registry</h4>
              {data.brands.length === 0 ? (
                <p className="text-xs text-zinc-400">No brands configured.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.brands.map((brand, i) => (
                    <div key={i} className="text-xs bg-zinc-50 rounded-lg px-3 py-2">
                      <span className="font-semibold text-zinc-700">{brand.name}</span>
                      {(brand.branches ?? []).length > 0 ? (
                        <div className="mt-1 pl-3 space-y-0.5">
                          {(brand.branches ?? []).map((br, j) => (
                            <div key={j} className="flex items-center gap-2 text-zinc-500">
                              <span className="font-mono font-bold text-zinc-600">{br.code}</span>
                              <span className="text-zinc-400">\u2192</span>
                              <span>{br.name}</span>
                            </div>
                          ))}
                        </div>
                      ) : <span className="ml-2 text-zinc-400">(no branches)</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h4 className="text-sm font-bold text-zinc-700 mb-3">Contracts</h4>
              {data.settings.contracts.length === 0 ? (
                <p className="text-xs text-zinc-400">No contracts configured.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.settings.contracts.map((c, i) => (
                    <span key={i} className="text-xs bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-lg font-medium">
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h4 className="text-sm font-bold text-zinc-700 mb-3">File Number Config</h4>
              <div className="space-y-2 text-xs text-zinc-600">
                <div className="flex justify-between">
                  <span>Starting number</span>
                  <span className="font-mono font-bold text-zinc-900">{data.settings.fileNoStart}</span>
                </div>
                <div className="flex justify-between">
                  <span>Padding digits</span>
                  <span className="font-mono font-bold text-zinc-900">{data.settings.fileNoPadding}</span>
                </div>
                <div className="flex justify-between">
                  <span>Card header</span>
                  <span className="font-bold text-zinc-900">{data.settings.cardHeader}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// App Silo Selector — choose which module to inspect
// ---------------------------------------------------------------------------

function AppSiloSelector({
  data,
  currentUser,
}: {
  data: GodModeSnapshot;
  currentUser: User | null;
}) {
  const navigate    = useNavigate();
  const org         = data.org!;
  const enabledApps: string[] = org.enabledApps ?? [];
  const [menuProfiles, setMenuProfiles] = useState<MenuProfile[]>([]);

  useEffect(() => {
    writeInspectionLog(currentUser, org.id, org.name, '');
    getRepository().getAll<MenuProfile>('menu-profiles').then(all => setMenuProfiles(all.filter(p => p.orgId === org.id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  return (
    <div className="flex flex-col min-h-screen bg-zinc-50">
      <GodModeContextBar
        orgName={org.name}
        onExit={() => navigate('/saas-admin/organizations')}
      />
      <div className="p-8 max-w-5xl mx-auto w-full">

        {/* Header */}
        <div className="mb-8">
          <h2 className="text-xl font-black text-zinc-900">Installed Modules</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Select a module to inspect{' '}
            <span className="font-semibold text-zinc-700">{org.name}</span>&apos;s data.
          </p>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Riders',   value: data.riders.length },
            { label: 'Users',    value: data.users.length },
            { label: 'Requests', value: data.requests.length },
            { label: 'Plan',     value: org.plan },
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
              <p className="text-xs text-zinc-500 font-medium">{stat.label}</p>
              <p className="text-xl font-black text-zinc-900 mt-0.5">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Module Connections */}
        {(() => {
          const connected = menuProfiles.filter(p => p.connectedModules && p.connectedModules.length > 0);
          if (connected.length === 0) return null;
          const getModName = (id: string) => PLATFORM_MODULES.find(m => m.id === id)?.name ?? id;
          return (
            <div className="bg-white border border-amber-200 rounded-xl p-5 mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Link2 size={14} className="text-amber-600" />
                <h3 className="text-sm font-bold text-zinc-900">Active Module Connections</h3>
                <span className="ml-auto text-[9px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">{connected.length} linked menu{connected.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2">
                {connected.map(p => (
                  <div key={p.id} className="flex items-center gap-3 bg-zinc-50 rounded-lg px-3 py-2">
                    <BookOpen size={14} className="text-indigo-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-zinc-700">{p.name}</span>
                      <span className="text-xs text-zinc-400 ml-1">({p.brandName})</span>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {p.connectedModules!.map(mId => (
                        <span key={mId} className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">🔗 {getModName(mId)}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Menu Profiles — Force Publish Toggle */}
        {menuProfiles.length > 0 && (
          <div className="bg-white border border-indigo-200 rounded-xl p-5 mb-8">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen size={14} className="text-indigo-600" />
              <h3 className="text-sm font-bold text-zinc-900">Menu Profiles — Live Status Control</h3>
              <span className="ml-auto text-[9px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">{menuProfiles.length} menu{menuProfiles.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-2">
              {menuProfiles.map(p => (
                <div key={p.id} className="flex items-center gap-3 bg-zinc-50 rounded-lg px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold text-zinc-700">{p.name}</span>
                    <span className="text-xs text-zinc-400 ml-1">({p.brandName})</span>
                    <span className={cn('ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full border',
                      p.status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : p.status === 'ARCHIVED' ? 'bg-slate-50 text-slate-500 border-slate-200'
                        : 'bg-zinc-100 text-zinc-600 border-zinc-200'
                    )}>{p.status}</span>
                  </div>
                  {p.status !== 'ARCHIVED' && (
                    <LiveToggleSwitch
                      isLive={p.status === 'PUBLISHED'}
                      size="sm"
                      showLabel={false}
                      onToggle={async (goLive) => {
                        try {
                          if (goLive) {
                            await apiFetch(`/api/menu/profiles/${p.id}/publish`, { method: 'POST' });
                          } else {
                            await apiFetch(`/api/menu/profiles/${p.id}/unpublish`, { method: 'POST' });
                          }
                          // Refresh profiles list
                          const all = await getRepository().getAll<MenuProfile>('menu-profiles');
                          setMenuProfiles(all.filter(mp => mp.orgId === org.id));
                        } catch { /* silent */ }
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Promotional Items — Financial Breakdown */}
        {(() => {
          const promoItems = menuProfiles.flatMap(p =>
            p.categories.flatMap(c => c.items.filter(i => i.campaignMeta).map(i => ({ ...i, profileName: p.name, brandName: p.brandName })))
          );
          if (promoItems.length === 0) return null;
          return (
            <div className="bg-white border border-emerald-200 rounded-xl p-5 mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Banknote size={14} className="text-emerald-600" />
                <h3 className="text-sm font-bold text-zinc-900">Promotional Items — Financial Breakdown</h3>
                <span className="ml-auto text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{promoItems.length} item{promoItems.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="py-2 pr-3 font-bold text-slate-500">Item</th>
                      <th className="py-2 pr-3 font-bold text-slate-500">Menu / Brand</th>
                      <th className="py-2 pr-3 font-bold text-slate-500">Campaign</th>
                      <th className="py-2 pr-3 font-bold text-slate-500 text-right">Original</th>
                      <th className="py-2 pr-3 font-bold text-slate-500 text-right">Promo</th>
                      <th className="py-2 pr-3 font-bold text-slate-500 text-right">Discount</th>
                      <th className="py-2 pr-3 font-bold text-slate-500 text-right">Sterling Share</th>
                      <th className="py-2 pr-3 font-bold text-slate-500 text-right">Partner Share</th>
                      <th className="py-2 font-bold text-slate-500">Funding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoItems.map(item => {
                      const fb = getFinancialBreakdown(item);
                      if (!fb) return null;
                      const expired = Date.now() > (item.campaignMeta?.endDate ?? 0);
                      return (
                        <tr key={item.id} className={cn('border-b border-slate-50', expired && 'opacity-50')}>
                          <td className="py-2 pr-3 font-semibold text-zinc-800">{item.nameEN}{expired && <span className="ml-1 text-[9px] text-red-500 font-bold">EXPIRED</span>}</td>
                          <td className="py-2 pr-3 text-zinc-500">{item.profileName} <span className="text-zinc-400">({item.brandName})</span></td>
                          <td className="py-2 pr-3 text-indigo-600 font-semibold">{item.campaignMeta?.campaignName}</td>
                          <td className="py-2 pr-3 text-right text-zinc-500 line-through">{fb.originalPrice.toFixed(2)}</td>
                          <td className="py-2 pr-3 text-right font-bold text-emerald-600">{fb.promoPrice.toFixed(2)}</td>
                          <td className="py-2 pr-3 text-right text-red-600 font-semibold">-{fb.discountAmount.toFixed(2)}</td>
                          <td className="py-2 pr-3 text-right text-indigo-600 font-semibold">{fb.sterlingShare.toFixed(2)}</td>
                          <td className="py-2 pr-3 text-right text-amber-600 font-semibold">{fb.partnerShare.toFixed(2)}</td>
                          <td className="py-2">
                            <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full', fb.fundingType === 'FULLY_FUNDED' ? 'bg-amber-50 text-amber-700 border border-amber-200' : fb.fundingType === 'CO_FUNDED' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-zinc-50 text-zinc-600 border border-zinc-200')}>
                              {fb.fundingType === 'FULLY_FUNDED' ? 'Partner' : fb.fundingType === 'CO_FUNDED' ? 'Co-Funded' : 'Self'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Module cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {APP_REGISTRY.map(app => {
            const isEnabled = enabledApps.includes(app.id);
            const isLive    = app.status === 'live';
            const Icon      = MODULE_ICONS[app.id] ?? Package;
            return (
              <div
                key={app.id}
                className={cn(
                  'bg-white border rounded-xl p-5 flex flex-col gap-3',
                  isLive && isEnabled
                    ? 'border-indigo-200 shadow-sm hover:shadow-md transition-shadow'
                    : 'border-zinc-200 opacity-60',
                )}
              >
                <div className="flex items-start justify-between">
                  <div className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center',
                    isLive && isEnabled ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-400',
                  )}>
                    <Icon size={20} />
                  </div>
                  {isLive && isEnabled   && (
                    <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      ENABLED
                    </span>
                  )}
                  {isLive && !isEnabled  && (
                    <span className="text-[10px] font-bold bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full">
                      NOT LICENSED
                    </span>
                  )}
                  {!isLive && (
                    <span className="text-[10px] font-bold bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">
                      COMING SOON
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-zinc-900">{app.name}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{app.description}</p>
                </div>
                <button
                  disabled={!isLive || !isEnabled}
                  onClick={() =>
                    navigate('/saas-admin/inspect/' + org.id + '/' + app.id)
                  }
                  className={cn(
                    'mt-auto flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs font-bold transition-colors',
                    isLive && isEnabled
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
                  )}
                >
                  {isLive && isEnabled  && <><Eye size={12} /> Inspect Data</>}
                  {isLive && !isEnabled && <><Lock size={12} /> Not Licensed</>}
                  {!isLive             && <><Clock size={12} /> Coming Soon</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// God Mode Campaign View — read-only campaign submission inspector
// ---------------------------------------------------------------------------

// ── Dynamic stage color palette for God Mode (cycles for any number of departments) ──
const GM_STAGE_PALETTE: { color: string; bg: string; border: string }[] = [
  { color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  { color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  { color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
  { color: 'text-teal-700',   bg: 'bg-teal-50',   border: 'border-teal-200' },
  { color: 'text-rose-700',   bg: 'bg-rose-50',   border: 'border-rose-200' },
  { color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  { color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
  { color: 'text-cyan-700',   bg: 'bg-cyan-50',   border: 'border-cyan-200' },
];
const GM_DONE_META = { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };

function buildGMStageMeta(stages: string[]): Record<string, { color: string; bg: string; border: string }> {
  const meta: Record<string, { color: string; bg: string; border: string }> = {};
  stages.forEach((stage, i) => {
    meta[stage] = GM_STAGE_PALETTE[i % GM_STAGE_PALETTE.length];
  });
  meta['DONE'] = GM_DONE_META;
  return meta;
}

const GM_STATUS_PILL: Record<SubmissionStatus, string> = {
  [SubmissionStatus.PENDING]:          'bg-amber-50 text-amber-700 border-amber-200',
  [SubmissionStatus.IN_REVIEW]:        'bg-blue-50 text-blue-700 border-blue-200',
  [SubmissionStatus.APPROVED]:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  [SubmissionStatus.DEPLOYMENT_READY]: 'bg-violet-50 text-violet-700 border-violet-200',
  [SubmissionStatus.POSTPONED]:        'bg-orange-50 text-orange-700 border-orange-200',
  [SubmissionStatus.COMPLETED]:        'bg-teal-50 text-teal-700 border-teal-200',
  [SubmissionStatus.REJECTED]:         'bg-zinc-100 text-zinc-700 border-zinc-300',
};

function GodModeCampaignView({ orgId }: { orgId: string }) {
  const repo = getRepository();
  const [submissions, setSubmissions] = useState<CampaignSubmission[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Dispatch state
  const [dispatchTarget, setDispatchTarget] = useState<CampaignReadiness | null>(null);
  const [dispatchConfig, setDispatchConfig] = useState<DispatchConfig>({ ...DEFAULT_DISPATCH_CONFIG });
  const [generating, setGenerating] = useState(false);
  const [dispatchTab, setDispatchTab] = useState<'readiness' | 'menu'>('menu');

  // Dynamic department stages from registry
  const dynamicStages = (() => {
    const campaignDepts = departments.filter(d => (d.linkedModules ?? []).includes('aggregator'));
    if (campaignDepts.length > 0) return campaignDepts.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(d => d.name);
    return [...DEPARTMENT_STAGES];
  })();
  const GM_STAGE_META = buildGMStageMeta(dynamicStages);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [all, depts, camps, settings] = await Promise.all([
        repo.getCampaignSubmissions(),
        repo.getDepartments(),
        repo.getCampaigns(),
        repo.getCampaignSettings(),
      ]);
      // Filter by org if applicable
      setSubmissions(orgId ? all.filter(s => !s.orgId || s.orgId === orgId) : all);
      setCampaigns(camps);
      setBrands(settings.brands ?? []);
      setDepartments(depts);
      setLoading(false);
    })();
  }, [orgId]);

  // Campaign readiness for dispatch
  const campaignReadiness: CampaignReadiness[] = (() => {
    const campIdsWithSubs = new Set(
      submissions
        .filter(s => s.status !== SubmissionStatus.REJECTED)
        .map(s => s.campaignId)
    );
    return Array.from(campIdsWithSubs)
      .map(cid => {
        const camp = campaigns.find(c => c.id === cid);
        if (!camp) return null;
        return computeCampaignReadiness(camp, submissions, brands);
      })
      .filter(Boolean) as CampaignReadiness[];
  })();

  const handleGenerateDispatch = async () => {
    if (!dispatchTarget) return;
    setGenerating(true);
    try {
      const result = await DispatchService.generate(
        dispatchTarget.campaign,
        dispatchTarget.readySubmissions,
        dispatchTarget.brands,
        dispatchConfig,
      );
      setDispatchTarget(null);
      // No toast available in GodMode — alert as fallback
      alert(`📦 Dispatch Package Ready!\n${result.brandCount} Brand(s) · ${result.itemCount} Items${result.imageCount > 0 ? ` · ${result.imageCount} Images` : ''} included.`);
    } catch (err) {
      console.error('Dispatch generation error:', err);
      alert('Export Failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const counts = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === SubmissionStatus.PENDING || s.status === SubmissionStatus.IN_REVIEW).length,
    approved: submissions.filter(s => s.status === SubmissionStatus.APPROVED || s.status === SubmissionStatus.COMPLETED).length,
    completed: submissions.filter(s => s.status === SubmissionStatus.COMPLETED).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total', value: counts.total, color: 'bg-zinc-900 text-white' },
          { label: 'Pending', value: counts.pending, color: 'bg-amber-50 text-amber-700 border border-amber-200' },
          { label: 'Approved', value: counts.approved, color: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
          { label: 'Completed', value: counts.completed, color: 'bg-teal-50 text-teal-700 border border-teal-200' },
        ].map(s => (
          <div key={s.label} className={cn("rounded-xl px-4 py-3", s.color)}>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">{s.label}</p>
            <p className="text-2xl font-black mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <Megaphone size={32} className="mx-auto mb-3 text-zinc-300" />
          <p className="text-sm">No campaign submissions found for this organisation.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Campaign</th>
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand</th>
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Submitter</th>
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Stage</th>
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Status</th>
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Items</th>
                <th className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {submissions.map(sub => {
                const stageMeta = GM_STAGE_META[sub.currentStage] ?? GM_STAGE_META['DONE'];
                return (
                  <React.Fragment key={sub.id}>
                    <tr
                      className="hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => setExpanded(expanded === sub.id ? null : sub.id)}
                    >
                      <td className="px-4 py-3 text-sm font-semibold text-zinc-900">{sub.campaignName}</td>
                      <td className="px-4 py-3 text-sm text-zinc-500">{sub.brandName}</td>
                      <td className="px-4 py-3 text-sm text-zinc-500">{sub.submitterName}</td>
                      <td className="px-4 py-3">
                        <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold border", stageMeta.bg, stageMeta.color, stageMeta.border)}>
                          {sub.currentStage}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border", GM_STATUS_PILL[sub.status])}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-zinc-500">
                        {sub.items.filter(it => it.itemStatus !== 'DISCARDED').length}
                        {sub.items.some(it => it.itemStatus === 'DISCARDED') && (
                          <span className="ml-1 text-[10px] text-red-400">({sub.items.filter(it => it.itemStatus === 'DISCARDED').length} discarded)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{new Date(sub.createdAt).toLocaleDateString()}</td>
                    </tr>

                    {/* Expandable row with items + review history */}
                    {expanded === sub.id && (
                      <tr>
                        <td colSpan={7} className="bg-zinc-50 px-6 py-4">
                          <div className="space-y-4">
                            {/* Pipeline */}
                            <div className="flex items-center gap-1">
                              {dynamicStages.map((stage, i) => {
                                const completed = sub.reviewHistory.some(r => r.departmentName === stage && r.action === 'APPROVE');
                                const returned = sub.reviewHistory.some(r => r.departmentName === stage && r.action === 'UNLOCK');
                                const stMeta = GM_STAGE_META[stage] ?? { color: 'text-zinc-700', bg: 'bg-zinc-50', border: 'border-zinc-200' };
                                return (
                                  <React.Fragment key={stage}>
                                    <div className={cn(
                                      "flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[10px] font-bold",
                                      completed ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                      returned ? "bg-amber-50 text-amber-700 border-amber-200" :
                                      cn(stMeta.bg, stMeta.color, stMeta.border)
                                    )}>
                                      {completed ? <CheckCircle2 size={12} /> : returned ? <Unlock size={12} /> : <Clock size={12} />}
                                      {stage}
                                    </div>
                                    {i < dynamicStages.length - 1 && <ArrowRight size={12} className="text-zinc-300 shrink-0" />}
                                  </React.Fragment>
                                );
                              })}
                              <ArrowRight size={12} className="text-zinc-300 shrink-0" />
                              <div className={cn(
                                "flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[10px] font-bold",
                                sub.currentStage === 'DONE' ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-400 border-zinc-200"
                              )}>
                                {sub.currentStage === 'DONE' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                                DONE
                              </div>
                            </div>

                            {/* Items */}
                            <div>
                              <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Items</p>
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-2">
                                  {sub.items.filter(it => it.itemStatus !== 'DISCARDED').map(item => (
                                    <span key={item.id} className={cn(
                                      "px-2.5 py-1 bg-white border rounded text-xs text-zinc-700 inline-flex items-center gap-1",
                                      (item as any).menuLinked ? "border-teal-200" : "border-zinc-200"
                                    )}>
                                      {(item as any).menuLinked && <Link2 size={10} className="text-teal-500" />}
                                      {item.name} — <span className="font-mono">{item.price.toFixed(2)}</span>
                                      {(item as any).menuLinked && (item as any).originalMenuPrice != null && item.price !== (item as any).originalMenuPrice && (
                                        <span className="text-[9px] text-amber-600 ml-1">(was {(item as any).originalMenuPrice.toFixed(2)})</span>
                                      )}
                                    </span>
                                  ))}
                                  {sub.items.filter(it => it.itemStatus === 'DISCARDED').map(item => (
                                    <span key={item.id} className="px-2.5 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-400 line-through flex items-center gap-1">
                                      <Ban size={10} />{item.name} — <span className="font-mono">{item.price.toFixed(2)}</span>
                                    </span>
                                  ))}
                                </div>
                                {/* Imported modifiers for menu-linked items */}
                                {sub.items.some(it => (it as any).menuLinked && (it as any).importedOptions?.length > 0) && (
                                  <div className="mt-2 space-y-1.5">
                                    <p className="text-[8px] font-bold text-indigo-400 uppercase tracking-widest">Menu-Linked Modifiers</p>
                                    {sub.items.filter(it => (it as any).menuLinked && (it as any).importedOptions?.length > 0).map(item => (
                                      <div key={item.id} className="px-2.5 py-1.5 bg-indigo-50/50 border border-indigo-100 rounded text-[10px]">
                                        <span className="font-semibold text-indigo-700">{item.name}:</span>
                                        {' '}
                                        {((item as any).importedOptions ?? []).map((og: any) => (
                                          <span key={og.id} className="text-zinc-500">
                                            {og.nameEN} ({og.options.map((o: any) => o.nameEN).join(', ')})
                                            {' '}
                                          </span>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Review history */}
                            {sub.reviewHistory.length > 0 && (
                              <div>
                                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Review Log</p>
                                <div className="space-y-1.5">
                                  {sub.reviewHistory.map((action, i) => (
                                    <div key={i} className={cn(
                                      "flex flex-col gap-1 px-3 py-2 rounded border text-xs",
                                      action.action === 'APPROVE' ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                        : action.action === 'UNLOCK' ? "bg-amber-50 border-amber-200 text-amber-700"
                                        : action.action === 'RESUBMIT' ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                                        : "bg-red-50 border-red-200 text-red-700"
                                    )}>
                                      <div className="flex items-center gap-2">
                                        {action.action === 'APPROVE' ? <CheckCircle2 size={12} /> : action.action === 'UNLOCK' ? <Unlock size={12} /> : action.action === 'RESUBMIT' ? <RefreshCw size={12} /> : <XCircle size={12} />}
                                        <span className="font-bold">[{action.departmentName}]</span>
                                        <span>{action.action === 'APPROVE' ? 'Approved' : action.action === 'UNLOCK' ? 'Unlocked' : action.action === 'RESUBMIT' ? 'Resubmitted' : 'Rejected'} by {action.reviewerName}</span>
                                        {action.note && <span className="text-zinc-500 ml-1">— {action.note}</span>}
                                        <span className="ml-auto text-zinc-400">{new Date(action.timestamp).toLocaleString()}</span>
                                      </div>
                                      {action.changeDetails && action.changeDetails.length > 0 && (
                                        <ul className="ml-6 space-y-0.5 text-indigo-600">
                                          {action.changeDetails.map((cd, ci) => (
                                            <li key={ci} className="flex items-center gap-1"><ChevronRight size={9} className="shrink-0" /> {cd}</li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dispatch Engine — God Mode can export any campaign */}
      {campaignReadiness.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
            <Download size={10} /> Dispatch Package Generator
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {campaignReadiness.filter(cr => cr.readyCount > 0).map(cr => (
              <button
                key={cr.campaign.id}
                onClick={() => { setDispatchConfig({ ...DEFAULT_DISPATCH_CONFIG, selectedBrandIds: cr.readySubmissions.map(s => s.brandId) }); setDispatchTarget(cr); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-white border-zinc-200 hover:bg-indigo-50 hover:border-indigo-300 text-left transition-all"
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                  <Package size={14} className="text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-zinc-900 truncate">{cr.campaign.name}</p>
                  <p className="text-[10px] text-zinc-500">{cr.readyCount} brand(s) · {cr.totalItems} items ready</p>
                </div>
                <Download size={14} className="text-zinc-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dispatch Configuration Modal */}
      {dispatchTarget && (() => {
        const selectedCount = dispatchConfig.selectedBrandIds.length || dispatchTarget.readySubmissions.length;
        const selectedItemCount = dispatchTarget.readySubmissions
          .filter(s => dispatchConfig.selectedBrandIds.length === 0 || dispatchConfig.selectedBrandIds.includes(s.brandId))
          .reduce((acc, s) => acc + s.items.filter(it => it.itemStatus !== 'DISCARDED').length, 0);

        const MENU_COL_OPTIONS: { key: keyof ColumnSelection; label: string }[] = [
          { key: 'itemName', label: 'Items' },
          { key: 'originalPrice', label: 'Original Price' },
          { key: 'discountedPrice', label: 'After Discount' },
          { key: 'description', label: 'Description' },
          { key: 'internalBrandId', label: 'Brand ID' },
          { key: 'itemId', label: 'Item ID' },
        ];

        const READINESS_COL_OPTIONS: { key: keyof ReadinessColumnSelection; label: string }[] = [
          { key: 'brandName', label: 'Brand Name' },
          { key: 'itemCount', label: 'Item Count' },
          { key: 'target', label: 'Target' },
          { key: 'balance', label: 'Balance' },
          { key: 'status', label: 'Status' },
          { key: 'currentStage', label: 'Current Stage' },
          { key: 'submittedBy', label: 'Submitted By' },
        ];

        const menuColCount = Object.values(dispatchConfig.columns).filter(Boolean).length;
        const readinessColCount = Object.values(dispatchConfig.readinessColumns).filter(Boolean).length;
        const showMenuCols = dispatchConfig.includeApprovedMenu || dispatchConfig.includeBrandSheets;
        const showReadinessCols = dispatchConfig.includeBrandReadiness;
        const showColumnConfig = showMenuCols || showReadinessCols;

        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { if (!generating) setDispatchTarget(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 bg-gradient-to-r from-slate-800 to-slate-900 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white"><Package size={20} /></div>
                <div>
                  <h3 className="text-base font-bold text-white">Configure Dispatch Package</h3>
                  <p className="text-xs text-slate-400 mt-0.5">"{dispatchTarget.campaign.name}" — {selectedCount} brand(s) · {selectedItemCount} items</p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Brand Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand Selection</p>
                  <button
                    onClick={() => {
                      const allIds = dispatchTarget.readySubmissions.map(s => s.brandId);
                      const allSelected = allIds.every(id => dispatchConfig.selectedBrandIds.includes(id));
                      setDispatchConfig(prev => ({ ...prev, selectedBrandIds: allSelected ? [] : allIds }));
                    }}
                    className="text-[9px] font-bold text-violet-600 hover:text-violet-700"
                  >
                    {dispatchTarget.readySubmissions.every(s => dispatchConfig.selectedBrandIds.includes(s.brandId)) ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {dispatchTarget.readySubmissions.map(sub => {
                    const isSelected = dispatchConfig.selectedBrandIds.includes(sub.brandId);
                    return (
                      <button key={sub.brandId} onClick={() => setDispatchConfig(prev => ({
                        ...prev, selectedBrandIds: isSelected ? prev.selectedBrandIds.filter(id => id !== sub.brandId) : [...prev.selectedBrandIds, sub.brandId],
                      }))} className={cn("flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all", isSelected ? "bg-violet-50 border-violet-300 ring-1 ring-violet-200" : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60")}>
                        <div className={cn("w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all", isSelected ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-300")}>
                          {isSelected && <Check size={9} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold text-zinc-900 truncate">{sub.brandName}</p>
                          <p className="text-[8px] text-zinc-400">{sub.items.filter(it => it.itemStatus !== 'DISCARDED').length} items</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sheet Tabs & Options (centered) */}
              <div>
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2 text-center">Sheets &amp; Options</p>
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  {([
                    { key: 'includeOverview' as const, label: 'Campaign Overview' },
                    { key: 'includeBrandReadiness' as const, label: 'Brand Readiness' },
                    { key: 'includeApprovedMenu' as const, label: 'Approved Menu Items' },
                    { key: 'includeBrandSheets' as const, label: 'Brand-Wise Sheets' },
                    { key: 'includeImages' as const, label: 'Include Images' },
                    { key: 'useRelationalModifierCodes' as const, label: 'Relational Modifier Codes' },
                  ] as const).map(opt => {
                    const isOn = dispatchConfig[opt.key];
                    return (
                      <button key={opt.key} onClick={() => setDispatchConfig(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))} className={cn("flex items-center gap-1.5 px-3 py-2 rounded-xl border text-left transition-all", isOn ? "bg-violet-50 border-violet-300 ring-1 ring-violet-200" : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60")}>
                        <div className={cn("w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 border transition-all", isOn ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-300")}>
                          {isOn && <Check size={8} />}
                        </div>
                        <p className="text-[10px] font-bold text-zinc-700">{opt.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Column Configuration Tabs (conditional) */}
              {showColumnConfig && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mr-auto">Column Configuration</p>
                    {showReadinessCols && (
                      <button onClick={() => setDispatchTab('readiness')} className={cn("px-2.5 py-1 rounded-lg text-[9px] font-bold transition-all border", dispatchTab === 'readiness' ? "bg-slate-800 text-white border-slate-700" : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100")}>
                        Readiness ({readinessColCount})
                      </button>
                    )}
                    {showMenuCols && (
                      <button onClick={() => setDispatchTab('menu')} className={cn("px-2.5 py-1 rounded-lg text-[9px] font-bold transition-all border", dispatchTab === 'menu' ? "bg-emerald-600 text-white border-emerald-500" : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100")}>
                        Menu Items ({menuColCount})
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {dispatchTab === 'readiness' && showReadinessCols ? (
                      READINESS_COL_OPTIONS.map(col => {
                        const isOn = dispatchConfig.readinessColumns[col.key];
                        return (
                          <button key={col.key} onClick={() => setDispatchConfig(prev => ({ ...prev, readinessColumns: { ...prev.readinessColumns, [col.key]: !prev.readinessColumns[col.key] } }))} className={cn("flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all", isOn ? "bg-slate-50 border-slate-300 ring-1 ring-slate-200" : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60")}>
                            <div className={cn("w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all", isOn ? "bg-slate-700 border-slate-700 text-white" : "bg-white border-zinc-300")}>
                              {isOn && <Check size={9} />}
                            </div>
                            <p className="text-[10px] font-bold text-zinc-700">{col.label}</p>
                          </button>
                        );
                      })
                    ) : showMenuCols ? (
                      MENU_COL_OPTIONS.map(col => {
                        const isOn = dispatchConfig.columns[col.key];
                        return (
                          <button key={col.key} onClick={() => setDispatchConfig(prev => ({ ...prev, columns: { ...prev.columns, [col.key]: !prev.columns[col.key] } }))} className={cn("flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all", isOn ? "bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200" : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60")}>
                            <div className={cn("w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all", isOn ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-zinc-300")}>
                              {isOn && <Check size={9} />}
                            </div>
                            <p className="text-[10px] font-bold text-zinc-700">{col.label}</p>
                          </button>
                        );
                      })
                    ) : null}
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2.5 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
                <Info size={14} className="text-slate-500 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-600">{dispatchConfig.includeImages ? 'ZIP package — Excel + /images folder. Filenames match item names.' : 'Standalone .xlsx with your selected columns and brands.'} Unchecked columns are fully removed.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50/50 shrink-0">
              <button onClick={() => setDispatchTarget(null)} disabled={generating} className="px-4 py-2.5 text-xs font-bold text-zinc-600 bg-white border border-zinc-200 hover:bg-zinc-100 rounded-xl transition-all disabled:opacity-40">Cancel</button>
              <button onClick={handleGenerateDispatch} disabled={generating || selectedCount === 0} className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 rounded-xl transition-all disabled:opacity-60">
                {generating ? <><RotateCcw size={13} className="animate-spin" /> Generating…</> : <><Download size={13} /> Generate &amp; Download</>}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// God Mode Module View — dispatches to module-specific read-only view
// ---------------------------------------------------------------------------

function GodModeModuleView({
  orgId,
  moduleId,
  currentUser,
}: {
  orgId: string;
  moduleId: string;
  currentUser: User | null;
}) {
  const data     = useGodModeData(orgId);
  const navigate  = useNavigate();
  const appDef     = APP_REGISTRY.find(a => a.id === moduleId);
  const moduleName = appDef?.name ?? moduleId;

  useEffect(() => {
    if (data.org && !data.loading) {
      writeInspectionLog(currentUser, orgId, data.org.name, moduleId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.org?.id, moduleId]);

  if (data.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Loading module data&hellip;</p>
        </div>
      </div>
    );
  }

  if (!data.org) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-zinc-500">
        <AlertCircle size={32} className="text-zinc-300" />
        <p className="text-sm">Organisation not found.</p>
        <button
          onClick={() => navigate('/saas-admin/organizations')}
          className="text-sm text-indigo-600 hover:underline"
        >
          &larr; Back to organisations
        </button>
      </div>
    );
  }

  const enabledApps: string[] = data.org.enabledApps ?? [];

  return (
    <div className="flex flex-col min-h-screen bg-zinc-50">
      <GodModeContextBar
        orgName={data.org.name}
        moduleName={moduleName}
        onExit={() => navigate('/saas-admin/organizations')}
      />
      {!enabledApps.includes(moduleId) ? (
        <div className="flex-1 flex items-center justify-center">
          <ModuleNotLicensedCard moduleName={moduleName} />
        </div>
      ) : moduleId === APP_IDS.RIDER_MANAGER ? (
        <div className="flex-1 bg-white overflow-hidden">
          <GodModeRiderView data={data} />
        </div>
      ) : moduleId === APP_IDS.CAMPAIGN_MANAGER ? (
        <div className="flex-1 bg-white overflow-hidden">
          <GodModeCampaignView orgId={orgId} />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-zinc-500 py-20">
            <FileText size={40} className="mx-auto mb-4 text-zinc-200" />
            <p className="text-sm">
              No inspector registered for{' '}
              <span className="font-bold">{moduleName}</span>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Export
// ---------------------------------------------------------------------------

export function GodModeInspectorPage({
  currentUser,
  writeAuditLog,
}: {
  currentUser: User | null;
  writeAuditLog: (action: string, extra?: Record<string, string>) => void;
}) {
  const params   = useParams<{ orgId: string; '*': string }>();
  const orgId    = params.orgId ?? '';
  const moduleId = (params['*'] ?? '').split('/')[0];

  const data     = useGodModeData(orgId);
  const navigate  = useNavigate();

  // Top-level audit log — fires once when the org loads
  useEffect(() => {
    if (data.org && !data.loading && orgId) {
      writeAuditLog(
        'SUPER_ADMIN_INSPECTION_START \u2014 God Mode opened for "' +
          data.org.name + '" (' + orgId + ')',
        { orgId, moduleId: moduleId || '' },
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data.org, data.loading]);

  if (data.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-50">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-zinc-500">Opening God Mode&hellip;</p>
        </div>
      </div>
    );
  }

  if (!data.org) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-zinc-50 text-zinc-500">
        <AlertCircle size={40} className="text-zinc-300" />
        <p className="text-sm">
          Organisation <span className="font-mono text-zinc-700">{orgId}</span> not found.
        </p>
        <button
          onClick={() => navigate('/saas-admin/organizations')}
          className="text-sm text-indigo-600 hover:underline"
        >
          &larr; Back to organisations
        </button>
      </div>
    );
  }

  if (moduleId) {
    return (
      <GodModeModuleView
        orgId={orgId}
        moduleId={moduleId}
        currentUser={currentUser}
      />
    );
  }

  return <AppSiloSelector data={data} currentUser={currentUser} />;
}
